import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../i18n'
import { useAuth } from '../utils/auth'
import SEO from '../components/SEO'
import './AuthPages.css'

/**
 * PayPal redirects the user here after successful subscription approval.
 * We poll the user profile until subscription_status flips to "active".
 */
function CheckoutSuccessPage() {
  const { t } = useTranslation()
  const { user, refreshUser, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    if (authLoading) return

    // If not logged in at all, send to login
    if (!user) {
      navigate('/login', { replace: true })
      return
    }

    // Poll up to 10 times (30s total) waiting for IPN to activate subscription
    let attempts = 0
    const maxAttempts = 10
    const interval = setInterval(async () => {
      attempts++
      await refreshUser()
      // We rely on the user object updating via context on next render
      if (attempts >= maxAttempts) {
        clearInterval(interval)
        setChecking(false)
      }
    }, 3000)

    return () => clearInterval(interval)
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading])

  // Watch for user becoming paid
  useEffect(() => {
    if (user?.is_paid) {
      setChecking(false)
    }
  }, [user?.is_paid])

  if (authLoading) {
    return (
      <div className="auth-page">
        <SEO title="Payment Successful" path="/checkout/success" noIndex />
        <div className="auth-card checkout-success-card">
          <div className="spinner" />
          <p>{t('checkoutSuccessLoading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-page">
      <SEO title="Payment Successful" path="/checkout/success" noIndex />
      <div className="auth-card checkout-success-card">
        {checking && !user?.is_paid ? (
          <>
            <div className="spinner" />
            <h2>{t('checkoutSuccessProcessing')}</h2>
            <p>{t('checkoutSuccessWait')}</p>
          </>
        ) : user?.is_paid ? (
          <>
            <div className="success-icon-large">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
            </div>
            <h2>{t('checkoutSuccessTitle')}</h2>
            <p>{t('checkoutSuccessDesc')}</p>
            <button
              className="btn btn-primary btn-large"
              onClick={() => navigate('/dashboard', { replace: true })}
            >
              {t('checkoutSuccessGoToDashboard')}
            </button>
          </>
        ) : (
          <>
            <h2>{t('checkoutSuccessPending')}</h2>
            <p>{t('checkoutSuccessPendingDesc')}</p>
            <button
              className="btn btn-primary btn-large"
              onClick={() => navigate('/dashboard', { replace: true })}
            >
              {t('checkoutSuccessGoToDashboard')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default CheckoutSuccessPage
